import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import useMixpanelEventAction from "../../config/mixpanel/useMixpanelEventAction";
import GA4EventAction from "../../config/GoogleAnalytics/useGA4EventAction";
import useAccountingSoftwareApi from "../useAccountingSoftwareApi";
import { commonErrMsg, login } from "../../locales/en";
import { isRecaptchaEnabled, showToast } from "../../utils/utils";
import {
  DISCONNECTED,
  EMAIL_REGEX,
  showToastType
} from "../../utils/constants/appConstants";
import { userLogin } from "../../redux/loginSlice";
import recordMarketingIdentifiers from "../../utils/recordMarketingIdentifiers";

import { FULFILLED } from "../../utils/constants/appConstants";
import useSentTwoFaQrcode from "./useSentTwoFaQrcode";
import useGetOnboardingData from "./useGetOnboardingData";
import useSubmitTwoFACode from "./useSubmitTwoFACode";
import {
  INVOICE,
  LASTCONNECTEDSOFTWARE,
  PARTNER_DASHBOARD
} from "../../route/paths";
import { persistUserRoles } from "../../utils/accountRoles";

const initialLoadingState = {
  loginBtn: false,
  sendTwoFaBtn: false,
  reSendTwoFaBtn: false
};

const initialValues = {
  email: "",
  password: "",
  showPassword: false,
  twoFARequired: false,
  twoFactorCode: ""
};

const initialErrors = {
  password: {},
  email: "",
  twoFactorCode: "",
  invalidMessage: "",
  invalidOTPMessage: "",
  errorMessage: "",
  reCaptcha: ""
};

export default function useLogin(environment, executeRecaptcha) {
  const { loginMixpanelEvent } = useMixpanelEventAction();
  const { loginGA4Event } = GA4EventAction();
  const {
    GetConnectedSoftware,
    FundedDataCheck,
    ToFundDataCheck,
    SingleConnectedSoftware
  } = useAccountingSoftwareApi();
  const [twoFAVerification, setTwoFAVerification] = useState(false);
  const [twoFAVerificationButton, setTwoFAVerificationButton] = useState(false);
  const [loginSubmitButton, setLoginSubmitButton] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [qrCodeVisible, setQrCodeVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(initialLoadingState);

  const [values, setValues] = useState(initialValues);

  const [errors, setErrors] = useState(initialErrors);

  const [success, setSuccess] = useState({
    twoFactorResent: ""
  });

  const navigate = useNavigate();

  const handleClickShowPassword = () => {
    setValues((prev) => ({ ...prev, showPassword: !prev.showPassword }));
  };

  const handleInputChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };
  const handleSentTwoFaQrcode = useSentTwoFaQrcode({
    values,
    errors,
    setErrors,
    setValues,
    setLoading,
    setTwoFAVerificationButton,
    setSuccess,
    setQrCodeVisible
  });

  const checkSoftwareConnected = useCallback(
    async (successMessage) => {
      try {
        const [
          connectedResult,
          fundedResult,
          toFundResult,
          singleConnectedResult
        ] = await Promise.allSettled([
          GetConnectedSoftware(),
          FundedDataCheck(),
          ToFundDataCheck(),
          SingleConnectedSoftware()
        ]);

        const isConnected =
          connectedResult.status === FULFILLED
            ? (connectedResult.value?.data?.data?.isConnected ?? false)
            : false;

        const hasData = (r) =>
          r?.status === FULFILLED &&
          ((a) => Array.isArray(a) && a.length)(r?.value?.data?.data);
        const isFundedDataFound = hasData(fundedResult);
        const isToFundDataFound = hasData(toFundResult);

        const singleConnectedData =
          singleConnectedResult.status === FULFILLED
            ? singleConnectedResult.value?.data?.data?.connectionStatus
            : null;

        if (singleConnectedData === DISCONNECTED) {
          navigate(LASTCONNECTEDSOFTWARE, {
            state: { successMessage }
          });
        } else if (isConnected || isFundedDataFound || isToFundDataFound) {
          navigate(INVOICE, {
            state: { successMessage }
          });
        } else {
          navigate(INVOICE, {
            state: { successMessage }
          });
        }
      } catch {
        showToast(commonErrMsg.item4, showToastType.error);
      }
    },
    [
      GetConnectedSoftware,
      FundedDataCheck,
      ToFundDataCheck,
      SingleConnectedSoftware,
      navigate
    ]
  );

  const handleGetOnboardingData = useGetOnboardingData({
    dispatch,
    navigate,
    checkSoftwareConnected
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    let isValid = true;
    const newErrors = { ...errors };
    if (values.email.trim() === "") {
      newErrors.email = login?.errorMSG.emailRequired;
      isValid = false;
    } else if (!EMAIL_REGEX.test(values.email)) {
      newErrors.email = login?.errorMSG.enterEmail;
      isValid = false;
    }
    if (values.password.trim() === "") {
      newErrors.password = [login?.errorMSG.enterPassword];
      isValid = false;
    }

    let recaptchaToken = null;

    if (isRecaptchaEnabled(environment)) {
      if (!executeRecaptcha) {
        newErrors.reCaptcha = commonErrMsg?.RecaptchaError;
        return;
      }
      recaptchaToken = await executeRecaptcha("login");
    }

    if (isRecaptchaEnabled(environment)) {
      if (!recaptchaToken) {
        newErrors.reCaptcha = login?.errorMSG.recaptcha;
        isValid = false;
      }
    }

    setErrors(newErrors);

    if (isValid) {
      setValues((prev) => ({ ...prev, error: "" }));

      const userData = {
        email: values.email,
        password: values.password,
        recaptchaResponse: recaptchaToken
      };

      try {
        setLoading((prev) => ({ ...prev, loginBtn: true }));
        const response = await dispatch(userLogin(userData)).unwrap();

        if (response.status === 200) {
          loginMixpanelEvent(response?.data?.data);
          // This is for GA4
          loginGA4Event(response.data.data.user.id);
          recordMarketingIdentifiers();
          const primaryUserId = response.data?.data?.user?.primaryUserId;
          const successMessage = response.data?.message || "";
          const userRoles = response.data?.data?.user?.roles ?? [];
          // Persist role names so the portal/client headers can offer the
          // partner<->client account toggle to dual-role users.
          persistUserRoles(userRoles);
          const isPartner = userRoles.some((r) => r.name === "ReferralPartner");
          if (isPartner) {
            navigate(PARTNER_DASHBOARD, { state: { successMessage } });
          } else if (primaryUserId !== null && primaryUserId !== undefined) {
            navigate(INVOICE, {
              state: { successMessage }
            });
          } else {
            handleGetOnboardingData(successMessage);
          }
        } else if (response.status === 202) {
          const dataParam = response.data.data.twoFactorUricode
            ? response.data.data.twoFactorUricode
            : null;
          if (dataParam != null) {
            setQrCodeUrl(response.data.data.twoFactorUricode);
            setQrCodeVisible(true);
          }
          setTwoFAVerification(true);
          setTwoFAVerificationButton(true);
          setLoginSubmitButton(false);
          setValues((prev) => ({ ...prev, twoFARequired: true }));
          setErrors(initialErrors);
          setErrorMessage("");
        }
      } catch (err) {
        if (!err?.status) {
          showToast(commonErrMsg.item4, showToastType.error);
        }
      } finally {
        setLoading((prev) => ({ ...prev, loginBtn: false }));
      }
    }
  };

  const handleSubmitTwoFACode = useSubmitTwoFACode({
    values,
    errors,
    setErrors,
    setSuccess,
    setLoading,
    loginMixpanelEvent,
    loginGA4Event,
    handleGetOnboardingData
  });

  return {
    values,
    errors,
    loading,
    success,
    twoFAVerification,
    twoFAVerificationButton,
    loginSubmitButton,
    qrCodeUrl,
    qrCodeVisible,
    errorMessage,
    handleInputChange,
    handleClickShowPassword,
    handleSentTwoFaQrcode,
    handleSubmit,
    handleSubmitTwoFACode,
    setValues,
    setErrors,
    setLoading,
    setQrCodeVisible
  };
}
